import type { ChangeEvent } from 'react';
import usePagination from '@mui/material/usePagination';
import PaginationPage from './PaginationPage';
import './Pagination.less';

type PaginationProps = {
  pagesCount: number;
  page?: number;
  isDisabled?: boolean;
  onChange: (activePageNumber: number) => void;
};

/**
 * A generic, uncontrolled (or optionally controlled) pagination component.
 * Manages current page state internally, then calls props.onChange to tell parent what page we're on.
 *
 * @param {PaginationProps} props - Pagination props
 * @param {number} props.pagesCount - Total number of pages.
 * @param {number} [props.page] - Current page (only use if controlling component from parent)
 * @param {boolean} [props.isDisabled] - If the pagination should be disabled or not.
 * @param {(activePageNumber: number) => void} props.onChange - Function called to let the parent know that the page has been updated.
 */
const Pagination = ({
  pagesCount,
  page,
  isDisabled,
  onChange,
}: PaginationProps) => {
  //  Throw away the event, which is not needed by the consumer.
  const handleChange = (_event: ChangeEvent<unknown>, nextPage: number) => onChange(nextPage);
  //  usePagination handles all the heavy lifting for us.
  const { items: pages } = usePagination({
    page: page || undefined,
    count: pagesCount,
    showFirstButton: false,
    showLastButton: false,
    disabled: isDisabled,
    onChange: handleChange,
  });

  return (
    <div className="fossa-pagination">
      {pages.map(({ page: currentPage, type, selected, disabled, onClick }) => (
        <PaginationPage
          key={`${type}__${currentPage || ''}`}
          page={currentPage}
          type={type}
          isActive={selected}
          isDisabled={disabled}
          onClick={onClick}
        />
      ))}
    </div>
  );
};

export default Pagination;
